import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext({
  isDark: false,
  toggleTheme: () => {},
  setDark: () => {},
  isAutoMode: true,
  setAutoMode: () => {}
});

export const useTheme = () => useContext(ThemeContext);

// Check if current time is in "night" hours (19:00 - 07:00)
const isNightTime = () => {
  const hour = new Date().getHours();
  return hour >= 19 || hour < 7;
};

export const ThemeProvider = ({ children }) => {
  const [isDark, setIsDark] = useState(() => {
    const savedManual = localStorage.getItem('pont10_dark_mode_manual');
    const savedAuto = localStorage.getItem('pont10_dark_mode_auto');
    
    // If user has manually set a preference, use that
    if (savedManual !== null) {
      return savedManual === 'true';
    }
    
    // Otherwise, check if auto mode is enabled (default: true)
    if (savedAuto === 'false') {
      return false;
    }
    
    // Auto mode: check time
    return isNightTime();
  });

  const [isAutoMode, setIsAutoMode] = useState(() => {
    const saved = localStorage.getItem('pont10_dark_mode_auto');
    return saved !== 'false'; // Default to true
  });

  const [manualOverride, setManualOverride] = useState(() => {
    return localStorage.getItem('pont10_dark_mode_manual') !== null;
  });

  // Check time periodically for auto mode
  useEffect(() => {
    if (!isAutoMode || manualOverride) return;

    const checkTime = () => {
      const shouldBeDark = isNightTime();
      setIsDark(shouldBeDark);
    };

    // Check every minute
    const interval = setInterval(checkTime, 60000);
    return () => clearInterval(interval);
  }, [isAutoMode, manualOverride]);

  useEffect(() => {
    // Apply dark class to document
    if (isDark) {
      document.documentElement.classList.add('dark');
      document.body.style.backgroundColor = '#1a1a1a';
    } else {
      document.documentElement.classList.remove('dark');
      document.body.style.backgroundColor = '#FDFCF8';
    }
  }, [isDark]);

  const toggleTheme = () => {
    const newValue = !isDark;
    setIsDark(newValue);
    setManualOverride(true);
    localStorage.setItem('pont10_dark_mode_manual', newValue.toString());
  };

  const setDark = (value) => {
    setIsDark(value);
    setManualOverride(true);
    localStorage.setItem('pont10_dark_mode_manual', value.toString());
  };

  const handleSetAutoMode = (value) => {
    setIsAutoMode(value);
    localStorage.setItem('pont10_dark_mode_auto', value.toString());
    
    if (value) {
      // Re-enable auto mode, clear manual override
      setManualOverride(false);
      localStorage.removeItem('pont10_dark_mode_manual');
      setIsDark(isNightTime());
    }
  };

  return (
    <ThemeContext.Provider value={{ 
      isDark, 
      toggleTheme, 
      setDark, 
      isAutoMode, 
      setAutoMode: handleSetAutoMode 
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
